import { motion, AnimatePresence } from 'framer-motion';
import { Home, Camera, Shield, Check, Zap, Video, View, Plane, TreePine } from 'lucide-react';
import { OrderFormData, ControleType, MarketingType } from '@/types/order';

interface Step2ServicesProps {
  data: OrderFormData;
  onChange: (data: Partial<OrderFormData>) => void;
}

export function Step2Services({ data, onChange }: Step2ServicesProps) {
  const handleControleToggle = (type: ControleType) => {
    const checked = !data.packControles.includes(type);
    const newControles = checked
      ? [...data.packControles, type]
      : data.packControles.filter((c) => c !== type);
    onChange({ packControles: newControles });
  };

  const handleMarketingToggle = (checked: boolean) => {
    if (checked) {
      onChange({ packMarketing: true, marketingType: 'bien' });
    } else {
      onChange({
        packMarketing: false,
        marketingType: undefined,
        marketingUpgrade: undefined,
        marketingDrone: false,
      });
    }
  };

  const handleMarketingTypeChange = (type: MarketingType) => {
    if (type === 'terrain') {
      // Terrain : drone inclus, pas d'upgrades vidéo/360
      onChange({ marketingType: type, marketingUpgrade: undefined, marketingDrone: false });
    } else {
      onChange({ marketingType: type });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      <div>
        <h3 className="text-3xl font-display font-bold text-navy-600 mb-2">Services</h3>
        <p className="text-lg text-navy-400">Sélectionnez les packs souhaités</p>
      </div>

      <div className="space-y-4">
        {/* ======================== PACK VISITES ======================== */}
        <PackCard
          icon={Home}
          title="Pack Visites"
          price="699€"
          priceSuffix="HTVA"
          description="Délégation complète de vos visites pendant 2 mois"
          badge="Garanti 48h"
          features={[
            'Disponibilité 6j/7',
            'Rapports détaillés après chaque session',
            'Panneau inclus (pose & dépose)',
          ]}
          isSelected={data.packVisites}
          onToggle={(checked) => onChange({ packVisites: checked })}
        />

        {/* ======================== PACK MARKETING ======================== */}
        <div
          className={`rounded-2xl border-2 transition-all ${
            data.packMarketing
              ? 'border-gold-500 bg-gold-50/30'
              : 'border-navy-100 bg-white hover:border-navy-200'
          }`}
        >
          <button
            type="button"
            onClick={() => handleMarketingToggle(!data.packMarketing)}
            className="w-full p-6 text-left"
          >
            <div className="flex items-start gap-4">
              <div
                className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-all ${
                  data.packMarketing
                    ? 'bg-gold-500 border-gold-500'
                    : 'border-navy-300 bg-white'
                }`}
              >
                {data.packMarketing && <Check className="w-4 h-4 text-white" />}
              </div>

              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  data.packMarketing ? 'bg-gold-100' : 'bg-cream'
                }`}
              >
                <Camera
                  className={`w-6 h-6 ${
                    data.packMarketing ? 'text-gold-600' : 'text-navy-400'
                  }`}
                />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div className="flex items-center gap-3">
                    <h4 className="text-lg font-semibold text-navy-600">Pack Marketing</h4>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gold-100 text-gold-700 text-[10px] font-semibold rounded-full">
                      <Zap className="w-3 h-3" />
                      Garanti 48h
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gold-600">
                      {data.marketingType === 'terrain' ? '149€' : '249€'}
                    </p>
                    <p className="text-xs text-navy-400">HTVA</p>
                  </div>
                </div>
                <p className="text-sm text-navy-400 mb-4">
                  Photos HD professionnelles et plans 2D de votre bien
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {['Photos HD professionnelles', 'Plans 2D', 'Livraison sous 48h'].map(
                    (feature, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-gold-500 flex-shrink-0" />
                        <span className="text-sm text-navy-600">{feature}</span>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </button>

          {/* Sous-options Marketing */}
          <AnimatePresence>
            {data.packMarketing && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden border-t border-gold-200"
              >
                <div className="p-6 space-y-5 bg-gold-50/20">
                  {/* Type de bien */}
                  <div>
                    <p className="text-sm font-medium text-navy-600 mb-3">Type de bien</p>
                    <div className="grid grid-cols-2 gap-3">
                      <label
                        className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                          data.marketingType === 'bien'
                            ? 'border-gold-500 bg-gold-50'
                            : 'border-navy-100 bg-white hover:border-navy-200'
                        }`}
                      >
                        <input
                          type="radio"
                          name="marketingType"
                          checked={data.marketingType === 'bien'}
                          onChange={() => handleMarketingTypeChange('bien')}
                          className="sr-only"
                        />
                        <Home className={`w-5 h-5 ${data.marketingType === 'bien' ? 'text-gold-600' : 'text-navy-400'}`} />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-navy-600">Bien immobilier</p>
                          <p className="text-xs text-navy-400">249€ HTVA</p>
                        </div>
                        {data.marketingType === 'bien' && (
                          <Check className="w-4 h-4 text-gold-600" />
                        )}
                      </label>

                      <label
                        className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                          data.marketingType === 'terrain'
                            ? 'border-gold-500 bg-gold-50'
                            : 'border-navy-100 bg-white hover:border-navy-200'
                        }`}
                      >
                        <input
                          type="radio"
                          name="marketingType"
                          checked={data.marketingType === 'terrain'}
                          onChange={() => handleMarketingTypeChange('terrain')}
                          className="sr-only"
                        />
                        <TreePine className={`w-5 h-5 ${data.marketingType === 'terrain' ? 'text-gold-600' : 'text-navy-400'}`} />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-navy-600">Terrain</p>
                          <p className="text-xs text-navy-400">149€ HTVA (drone inclus)</p>
                        </div>
                        {data.marketingType === 'terrain' && (
                          <Check className="w-4 h-4 text-gold-600" />
                        )}
                      </label>
                    </div>
                  </div>

                  {/* Upgrades - uniquement pour "bien" */}
                  {data.marketingType === 'bien' && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-3"
                    >
                      <p className="text-sm font-medium text-navy-600">
                        Options supplémentaires
                      </p>

                      {/* Vidéo */}
                      <UpgradeOption
                        icon={Video}
                        label="Vidéo immobilière"
                        price="+99€"
                        isSelected={data.marketingUpgrade === 'video'}
                        onToggle={() =>
                          onChange({
                            marketingUpgrade:
                              data.marketingUpgrade === 'video' ? undefined : 'video',
                          })
                        }
                      />

                      {/* Visite virtuelle 360° */}
                      <UpgradeOption
                        icon={View}
                        label="Visite virtuelle 360°"
                        price="+99€"
                        isSelected={data.marketingUpgrade === '360'}
                        onToggle={() =>
                          onChange({
                            marketingUpgrade:
                              data.marketingUpgrade === '360' ? undefined : '360',
                          })
                        }
                      />

                      {/* Drone */}
                      <UpgradeOption
                        icon={Plane}
                        label="Photos drone"
                        price="+149€"
                        isSelected={data.marketingDrone === true}
                        onToggle={() => onChange({ marketingDrone: !data.marketingDrone })}
                      />
                    </motion.div>
                  )}

                  {/* Info terrain */}
                  {data.marketingType === 'terrain' && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex items-center gap-2 p-3 bg-gold-50 rounded-lg border border-gold-200"
                    >
                      <Plane className="w-4 h-4 text-gold-600 flex-shrink-0" />
                      <p className="text-sm text-gold-700">
                        Les photos drone sont incluses dans le Pack Marketing Terrain.
                      </p>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ======================== PACK CONTRÔLES ======================== */}
        <div
          className={`rounded-2xl border-2 transition-all ${
            data.packControles.length > 0
              ? 'border-gold-500 bg-gold-50/30'
              : 'border-navy-100 bg-white hover:border-navy-200'
          }`}
        >
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  data.packControles.length > 0 ? 'bg-gold-100' : 'bg-cream'
                }`}
              >
                <Shield
                  className={`w-6 h-6 ${
                    data.packControles.length > 0 ? 'text-gold-600' : 'text-navy-400'
                  }`}
                />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <h4 className="text-lg font-semibold text-navy-600">Pack Contrôles</h4>
                </div>
                <p className="text-sm text-navy-400 mb-5">
                  Certificats et contrôles légaux pour votre bien
                </p>

                {/* PEB */}
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => handleControleToggle('peb')}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all text-left ${
                      data.packControles.includes('peb')
                        ? 'border-gold-500 bg-gold-50'
                        : 'border-navy-100 bg-white hover:border-navy-200'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                        data.packControles.includes('peb')
                          ? 'bg-gold-500 border-gold-500'
                          : 'border-navy-300 bg-white'
                      }`}
                    >
                      {data.packControles.includes('peb') && (
                        <Check className="w-3.5 h-3.5 text-white" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-navy-600">Certificat PEB</p>
                      <p className="text-xs text-navy-400">
                        Certificat de performance énergétique
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-gold-600">279€</p>
                      <p className="text-xs text-navy-400">HTVA</p>
                    </div>
                  </button>

                  {/* Électricité */}
                  <button
                    type="button"
                    onClick={() => handleControleToggle('elec')}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all text-left ${
                      data.packControles.includes('elec')
                        ? 'border-gold-500 bg-gold-50'
                        : 'border-navy-100 bg-white hover:border-navy-200'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                        data.packControles.includes('elec')
                          ? 'bg-gold-500 border-gold-500'
                          : 'border-navy-300 bg-white'
                      }`}
                    >
                      {data.packControles.includes('elec') && (
                        <Check className="w-3.5 h-3.5 text-white" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-navy-600">Contrôle électricité</p>
                      <p className="text-xs text-navy-400">
                        Contrôle de conformité électrique
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-gold-600">149€</p>
                      <p className="text-xs text-navy-400">HTVA</p>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ======================== COMPOSANTS INTERNES ======================== */

interface PackCardProps {
  icon: React.ElementType;
  title: string;
  price: string;
  priceSuffix?: string;
  description: string;
  badge?: string;
  features: string[];
  isSelected: boolean;
  onToggle: (checked: boolean) => void;
}

function PackCard({
  icon: Icon,
  title,
  price,
  priceSuffix,
  description,
  badge,
  features,
  isSelected,
  onToggle,
}: PackCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl border-2 transition-all ${
        isSelected
          ? 'border-gold-500 bg-gold-50/30'
          : 'border-navy-100 bg-white hover:border-navy-200'
      }`}
    >
      <button
        type="button"
        onClick={() => onToggle(!isSelected)}
        className="w-full p-6 text-left"
      >
        <div className="flex items-start gap-4">
          <div
            className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-all ${
              isSelected ? 'bg-gold-500 border-gold-500' : 'border-navy-300 bg-white'
            }`}
          >
            {isSelected && <Check className="w-4 h-4 text-white" />}
          </div>

          <div
            className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
              isSelected ? 'bg-gold-100' : 'bg-cream'
            }`}
          >
            <Icon
              className={`w-6 h-6 ${isSelected ? 'text-gold-600' : 'text-navy-400'}`}
            />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4 mb-2">
              <div className="flex items-center gap-3">
                <h4 className="text-lg font-semibold text-navy-600">{title}</h4>
                {badge && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gold-100 text-gold-700 text-[10px] font-semibold rounded-full">
                    <Zap className="w-3 h-3" />
                    {badge}
                  </span>
                )}
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gold-600">{price}</p>
                {priceSuffix && (
                  <p className="text-xs text-navy-400">{priceSuffix}</p>
                )}
              </div>
            </div>
            <p className="text-sm text-navy-400 mb-4">{description}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-gold-500 flex-shrink-0" />
                  <span className="text-sm text-navy-600">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </button>
    </motion.div>
  );
}

interface UpgradeOptionProps {
  icon: React.ElementType;
  label: string;
  price: string;
  isSelected: boolean;
  onToggle: () => void;
}

function UpgradeOption({ icon: Icon, label, price, isSelected, onToggle }: UpgradeOptionProps) {
  return (
    <label
      className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
        isSelected
          ? 'border-gold-500 bg-gold-50'
          : 'border-navy-100 bg-white hover:border-navy-200'
      }`}
    >
      <div
        className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
          isSelected ? 'bg-gold-500 border-gold-500' : 'border-navy-300 bg-white'
        }`}
        onClick={(e) => {
          e.preventDefault();
          onToggle();
        }}
      >
        {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
      </div>
      <Icon className={`w-5 h-5 ${isSelected ? 'text-gold-600' : 'text-navy-400'}`} />
      <span className="flex-1 text-sm font-medium text-navy-600">{label}</span>
      <span className="text-sm font-bold text-gold-600">{price}</span>
    </label>
  );
}
